import React, { useState, useEffect, useRef } from 'react';
import { initializeTagStyles, getTagColor } from '../../styles/tagStyles';

// Inicializar estilos globais
initializeTagStyles();

interface TagOption {
  value: string;
  label: string;
}

interface TagsInputProps {
  selectedTags: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  id?: string;
  suggestedTags?: TagOption[];
}

const TagsInput: React.FC<TagsInputProps> = ({
  selectedTags = [],
  onChange,
  placeholder = 'Digite as tags e pressione Enter',
  id = 'tagsInput',
  suggestedTags
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<TagOption[]>([]);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Filtrar sugestões com base no valor de entrada
  useEffect(() => {
    const optionsToUse = suggestedTags || []; // Usar suggestedTags ou um array vazio se não houver sugestões

    if (inputValue.trim() === '') {
      // Se o input estiver vazio mas estiver em foco, mostrar as primeiras 20 sugestões
      if (showSuggestions) {
        const initialSuggestions = optionsToUse
          .filter((option: TagOption) => !selectedTags.includes(option.value))
          .slice(0, 20);
        setFilteredSuggestions(initialSuggestions);
      } else {
        setFilteredSuggestions([]);
      }
      return;
    }

    const filtered = optionsToUse.filter(
      (option: TagOption) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase()) &&
        !selectedTags.includes(option.value)
    );

    setFilteredSuggestions(filtered);
    setActiveSuggestionIndex(0);
  }, [inputValue, selectedTags, suggestedTags, showSuggestions]);

  // Fechar sugestões ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setShowSuggestions(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Navegar pelas sugestões com setas
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestionIndex(prev =>
        prev < filteredSuggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestionIndex(prev => (prev > 0 ? prev - 1 : 0));
    }
    // Selecionar sugestão com Enter
    else if (e.key === 'Enter' && showSuggestions && filteredSuggestions.length > 0) {
      e.preventDefault();
      addTag(filteredSuggestions[activeSuggestionIndex].value);
    }
    // Adicionar tag personalizada com Enter
    else if (e.key === 'Enter' && inputValue.trim() !== '') {
      e.preventDefault();
      const customTag = inputValue.trim().toLowerCase();
      addTag(customTag);
    }
    // Remover última tag com Backspace
    else if (e.key === 'Backspace' && inputValue === '' && selectedTags.length > 0) {
      removeTag(selectedTags[selectedTags.length - 1]);
    }
  };

  const addTag = (tag: string) => {
    if (!selectedTags.includes(tag) && tag.trim() !== '') {
      const newTags = [...selectedTags, tag];
      onChange(newTags);
      setInputValue('');
      setShowSuggestions(false);
    }
  };

  const removeTag = (tag: string) => {
    const newTags = selectedTags.filter(t => t !== tag);
    onChange(newTags);
  };

  const handleSuggestionClick = (tag: string) => {
    addTag(tag);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Determinar a classe de cor para uma tag
  const getTagColorClass = (tag: string): string => {
    return getTagColor(tag);
  };

  // Obter o rótulo de exibição para uma tag
  const getTagLabel = (tag: string): string => {
    // Verificar primeiro nas tags sugeridas, se existirem
    if (suggestedTags) {
      const suggestedOption = suggestedTags.find(opt => opt.value === tag);
      if (suggestedOption) return suggestedOption.label;
    }

    // Se não encontrou nas suggestedTags (ou se suggestedTags não existe), retorna a própria tag como label
    return tag;
  };

  return (
    <div className="relative">
      <div className="flex flex-wrap items-center p-2 border border-stroke bg-transparent rounded-lg dark:border-form-strokedark dark:bg-form-input focus-within:border-primary">
        {/* Tags selecionadas */}
        {selectedTags.map((tag) => (
          <div key={tag} className={`tag-chip ${getTagColorClass(tag)}`}>
            {getTagLabel(tag)}
            <span
              className="tag-delete"
              onClick={() => removeTag(tag)}
            >
              ×
            </span>
          </div>
        ))}

        {/* Input para novas tags */}
        <input
          ref={inputRef}
          type="text"
          id={id}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowSuggestions(true)}
          placeholder={selectedTags.length === 0 ? placeholder : ''}
          className="flex-grow outline-none bg-transparent py-1 px-2 min-w-[120px] placeholder:text-gray-400 dark:placeholder:text-gray-300"
        />
      </div>

      {/* Lista de sugestões */}
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="tag-suggestions absolute left-0 right-0 mt-1 z-50"
        >
          {filteredSuggestions.map((suggestion, index) => (
            <div
              key={suggestion.value}
              className={`tag-suggestion-item ${index === activeSuggestionIndex ? 'active' : ''}`}
              onClick={() => handleSuggestionClick(suggestion.value)}
            >
              {suggestion.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TagsInput;
